import { useEffect } from "react";
import {
  DailyLabellerStatsEntry,
  LabellerDetails,
  TimeRangeFilterMode,
  useGetCostLabellersQuery,
  useGetDailyLabellerStatsQuery,
} from "src/store/api/generatedApi";

const NO_LABELLERS: readonly LabellerDetails[] = [];
const NO_DAILY_STATS: readonly DailyLabellerStatsEntry[] = [];

export type OnLabellersLoaded = (
  tenantName: string,
  labellers: readonly LabellerDetails[],
  isLoading: boolean,
  isError: boolean
) => void;

export const TenantLabellersLoader = ({
  tenantName,
  start,
  end,
  filterMode,
  onLoaded,
}: Readonly<{
  tenantName: string;
  start: string | undefined;
  end: string | undefined;
  filterMode: TimeRangeFilterMode;
  onLoaded: OnLabellersLoaded;
}>) => {
  const { currentData, isLoading, isError } = useGetCostLabellersQuery({
    tenantName,
    start,
    end,
    filterMode,
  });

  useEffect(() => {
    onLoaded(tenantName, currentData ?? NO_LABELLERS, isLoading, isError);
  }, [tenantName, currentData, isLoading, isError, onLoaded]);

  return null;
};

export type OnDailyStatsLoaded = (
  tenantName: string,
  labellerId: string,
  dailyStats: readonly DailyLabellerStatsEntry[],
  isLoading: boolean,
  isFetching: boolean,
  isError: boolean
) => void;

export const TenantDailyStatsLoader = ({
  tenantName,
  labellerId,
  start,
  end,
  filterMode,
  sessionTypes,
  onLoaded,
}: Readonly<{
  tenantName: string;
  labellerId: string;
  start: string | undefined;
  end: string | undefined;
  filterMode: TimeRangeFilterMode;
  sessionTypes: ("labelling" | "reviewing")[];
  onLoaded: OnDailyStatsLoaded;
}>) => {
  // Not `data`: it retains the previous labeller's response, misattributing it.
  const { currentData, isLoading, isFetching, isError } =
    useGetDailyLabellerStatsQuery({
      tenantName,
      labellerId,
      start,
      end,
      filterMode,
      sessionTypes,
    });

  useEffect(() => {
    onLoaded(
      tenantName,
      labellerId,
      currentData?.daily_stats ?? NO_DAILY_STATS,
      isLoading,
      isFetching,
      isError
    );
  }, [
    tenantName,
    labellerId,
    currentData?.daily_stats,
    isLoading,
    isFetching,
    isError,
    onLoaded,
  ]);

  return null;
};
